import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { ScaleTime } from 'd3-scale';
import { addMinutes, max as maxDate, roundToNearestMinutes } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
    BLOCK_DEFAULTS,
    MIN_BLOCK_DURATION_MINUTES,
    MINUTES_IN_DAY,
    MINUTES_IN_WEEK,
} from '../constants';
import { calendarTimezone, checkCollision, dateToMilitary, getWeekWrappedMinutes } from '../utils';
import type { TimeWindow } from '../utils';
import type { BlockDragListeners, CalendarDay } from '../components/types';

interface DayTime {
    day: number;
    time: number;
}

function roundToBlockDuration(inputDate: Date): Date {
    return roundToNearestMinutes(inputDate, { nearestTo: MIN_BLOCK_DURATION_MINUTES });
}

function minutesToDayAndMilitaryTime(totalMinutes: number): DayTime {
    const day = Math.floor(totalMinutes / MINUTES_IN_DAY);
    const remainder = totalMinutes % MINUTES_IN_DAY;
    const hours = Math.floor(remainder / 60);
    const minutes = remainder % 60;
    const time = hours * 100 + minutes;
    return { day, time };
}

// drag: startDay: 6, startTime: 1000, endDay: 0, startTime: 1100
// will convert to:
// drag: startDay: 6, startTime: 1000, endDay: 7, startTime: 1100
// should clip to:
// block: startDay: 0, startTime: 900, endDay: 0, startTime: 1000

function getCollisionSnapPoint(drawingBlock: TimeWindow, blocks: TimeWindow[]): DayTime | null {
    const [dragStart, dragEnd] = getWeekWrappedMinutes(drawingBlock);

    const earliestBlockStart = blocks.reduce<number | null>((earliestBlockStartSoFar, block) => {
        function returnSmallest(newValue: number): number {
            return earliestBlockStartSoFar === null
                ? newValue
                : Math.min(earliestBlockStartSoFar, newValue);
        }

        const [blockStart, blockEnd] = getWeekWrappedMinutes(block);
        // On drag start we already have checked any collisions with existing objects,
        // so it's safe to assume dragStart can only be before or after other blocks.

        // If drag start is past block end, there's a possibility that
        // we are dragging from saturday (day 6) to sunday (day 7/0).
        if (dragStart > blockEnd) {
            // So, check if considering day 0 as day 7 will collide.
            if (dragEnd > blockStart + MINUTES_IN_WEEK) {
                return returnSmallest(blockStart);
            }
            // If not, no need to check any further collisions.
            return earliestBlockStartSoFar;
        }
        // Start of the drag is before the block end, so it should be
        // in a non week offset state. So only clip if drag end is
        // past block start.
        if (dragStart < blockEnd && dragEnd > blockStart) {
            return returnSmallest(blockStart);
        }

        return earliestBlockStartSoFar;
    }, null);

    if (earliestBlockStart !== null) {
        return minutesToDayAndMilitaryTime(earliestBlockStart);
    }
    // No overlapping block found.
    return null;
}

function resetCursorStyles(): void {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
}

interface UseBlockDragHandlersArgs {
    customTemplate: TimeWindow[];
    setDrawingBlock: (block?: TimeWindow | null) => void;
    drawingBlock?: TimeWindow | null;
    changeTemplate: (updater: (currentBlocks: TimeWindow[]) => TimeWindow[]) => void;
    openEditModal: (block: TimeWindow) => void;
    scalesPerDay: Map<CalendarDay, ScaleTime<number, number>>;
}

interface UseBlockDragHandlersResult extends BlockDragListeners {
    onMouseLeave: () => void;
}

export function useBlockDragHandlers({
    customTemplate,
    setDrawingBlock,
    drawingBlock,
    changeTemplate,
    openEditModal,
    scalesPerDay,
}: UseBlockDragHandlersArgs): UseBlockDragHandlersResult {
    const dragStartDateRef = useRef<Date | null>(null);

    useEffect(() => {
        if (drawingBlock) {
            document.body.style.cursor = 'move';
            document.body.style.userSelect = 'none';
        } else {
            resetCursorStyles();
        }

        return () => {
            // Ensure cleanup on unmount.
            resetCursorStyles();
        };
    }, [drawingBlock]);

    const getDateFromEvent = useCallback(
        (day: CalendarDay, event: ReactMouseEvent<HTMLDivElement>): Date => {
            const offsetX = event.clientX - event.currentTarget.getBoundingClientRect().left;
            const scale = scalesPerDay.get(day)!;
            return utcToZonedTime(scale.invert(offsetX), calendarTimezone);
        },
        [scalesPerDay]
    );

    // Starts block creation at clicked position and validates no overlapping blocks.
    const blockDragStart = useCallback(
        (day: CalendarDay, event: ReactMouseEvent<HTMLDivElement>) => {
            if (drawingBlock) return;
            const clickDate = roundToBlockDuration(getDateFromEvent(day, event));
            const clickMilitary = dateToMilitary(clickDate);
            const blockEndDate = addMinutes(clickDate, MIN_BLOCK_DURATION_MINUTES);
            const blockEndMilitary = dateToMilitary(blockEndDate);

            const newBlock: TimeWindow = {
                startDay: clickMilitary.day,
                startTime: clickMilitary.time,
                endDay: blockEndMilitary.day,
                endTime: blockEndMilitary.time,
                ...BLOCK_DEFAULTS,
            };

            // The block should not overlap with existing blocks.
            const hasCollision = checkCollision(newBlock, customTemplate);
            if (hasCollision) {
                return;
            }

            dragStartDateRef.current = clickDate;

            setDrawingBlock(newBlock);
        },
        [drawingBlock, getDateFromEvent, customTemplate, setDrawingBlock]
    );

    const submitDrawingBlock = useCallback(() => {
        if (!drawingBlock) return;

        changeTemplate((current) => [...current, drawingBlock]);

        // Cleanup the drawing block.
        setDrawingBlock(null);
        dragStartDateRef.current = null;

        // Open the modal for editing.
        openEditModal(drawingBlock);
    }, [openEditModal, changeTemplate, drawingBlock, setDrawingBlock]);

    // Handles block end time updates during drag and finalizes block creation.
    const setToDate = useCallback(
        (day: CalendarDay, event: ReactMouseEvent<HTMLDivElement>, isDragEnd = false) => {
            if (!drawingBlock || !dragStartDateRef.current) return;

            const roundedDate = roundToBlockDuration(getDateFromEvent(day, event));
            const minEndBlock = addMinutes(dragStartDateRef.current, MIN_BLOCK_DURATION_MINUTES);
            // If someone started drawing from 8:46, that will clip to 9:00. But the pointer is
            // still somewhere between 8:46 and 9:00 so it should jump to 9:30 in that state
            // till the pointer is past 9:30.
            const endDate = maxDate([roundedDate, minEndBlock]);

            const militaryToCheck = dateToMilitary(endDate);
            const blockToCheck: TimeWindow = {
                ...drawingBlock,
                endDay: militaryToCheck.day,
                endTime: militaryToCheck.time,
            };

            // Find the earliest collision point.
            const collisionPoint = getCollisionSnapPoint(blockToCheck, customTemplate);

            // If we found a collision point, use it instead of the mouse position.
            const finalMilitary = collisionPoint ?? militaryToCheck;

            const updatedBlock: TimeWindow = {
                ...drawingBlock,
                endDay: finalMilitary.day,
                endTime: finalMilitary.time,
            };

            setDrawingBlock(updatedBlock);

            if (isDragEnd) {
                submitDrawingBlock();
            }
        },
        [drawingBlock, getDateFromEvent, customTemplate, setDrawingBlock, submitDrawingBlock]
    );

    // Mouse move handler: Update the end date and time during drag.
    const blockDragMove = useCallback(
        (day: CalendarDay, event: ReactMouseEvent<HTMLDivElement>) => setToDate(day, event),
        [setToDate]
    );

    // Mouse up handler: Finalize the object update and stop dragging.
    const blockDragEnd = useCallback(
        (day: CalendarDay, event: ReactMouseEvent<HTMLDivElement>) => setToDate(day, event, true),
        [setToDate]
    );

    const onMouseLeave = useCallback(() => {
        submitDrawingBlock();
    }, [submitDrawingBlock]);

    return {
        blockDragStart,
        blockDragMove,
        blockDragEnd,
        onMouseLeave,
    };
}
